/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';

import { SuccessModal } from '@/components/ui/Modals/Success/SuccessModal';
import { CloseMobile } from '@/icons/index';
import { Button, Input, ErrorModal } from '@/ui/index';
import { hooks as fiberHooks } from '@/domains/fiber';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import { envVariables } from '@/constants/index';

import styles from './RequestNewAddress.module.scss';

const RequestNewAddress = ({
  active = false,
  closeModalAction = () => null,
  className,
  classNameInner,
  city,
  street,
  house,
}) => {
  const { t, lang } = useTranslation();
  const [captcha, setCaptcha] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const closeModal = (event) => {
    if (event.key === 'Escape') {
      closeModalAction();
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', closeModal);

    return () => {
      document.removeEventListener('keydown', closeModal);
    };
  }, [closeModal]);
  const [connectionRequest] = fiberHooks.useFiberConnectionRequest();
  const validationSchema = Yup.object({
    name: Yup.string().required(t('common:Required')),
    phone: Yup.string()
      .required(t('common:Required'))
      .length(9, t('common:Required')),
  });
  const houseDetailed = `${city?.name}, ${street}, ${house}`;
  const formik = useFormik({
    initialValues: {
      name: '',
      phone: '',
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        const data = {
          address: houseDetailed,
          name: values.name,
          type: 'individual',
          platform: 'bakcell_web',
          phone_number: values.phone,
          'g-recaptcha-response': captcha,
        };
        await connectionRequest({
          data,
        }).unwrap();

        setShowSuccess(true);
      } catch (error) {
        setShowError(true);
      }
    },
  });

  useEffect(() => {
    formik.resetForm();
    setCaptcha('');
    if (typeof window !== 'undefined' && window.grecaptcha) {
      window.grecaptcha.reset();
    }
  }, [active]);

  const onChange = (value) => {
    formik.setFieldValue('captcha', value);
    setCaptcha(value);
  };
  return (
    <div
      className={classNames([
        styles.wrap,
        { [styles.open]: active },
        className,
      ])}
      itemScope
      itemType='https://schema.org/Action'
    >
      <div className={classNames([styles.pinInner, classNameInner])}>
        <div className={styles.pinContent}>
          <h5 className={styles.title}>
            {t('fiber:modals.requestAddressTitle')}
          </h5>
          <p
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: t('fiber:modals.requestAddressText'),
            }}
          ></p>

          <div className={styles.address}>{houseDetailed}</div>

          <div className={styles.form_wrapper}>
            <Input
              onBlur={formik.handleBlur('name')}
              error={formik.touched.name && formik.errors.name}
              value={formik.values.name}
              onChange={(value) => {
                formik.setFieldValue('name', value);
              }}
              label={t('common:Fullname')}
              iconClass={styles.pin_input}
              data-testid='requestAdress-name-input'
            />
            <Input
              onBlur={formik.handleBlur('phone')}
              error={formik.touched.phone && formik.errors.phone}
              value={formik.values.phone}
              onChange={(value) => {
                const formattedPhone = formatPhoneNumber(value)
                  ?.replace(/[+-]/g, '')
                  .replace('994', '');
                formik.setFieldValue(
                  'phone',
                  formattedPhone.replace('994', ''),
                );
              }}
              label={t('common:Phone')}
              placeholder={'+994-XX-XXX-XX-XX'}
              mask={'+\\9\\9\\4-99-999-99-99'}
              iconClass={styles.pin_input}
              data-testid='requestAdress-phone-input'
            />
          </div>
          <span className={styles.terms_text}>
            {t('fiber:modals.modalTerms')}
          </span>
          <div className={styles.close} onClick={closeModalAction}>
            <CloseMobile />
          </div>
          <div className={styles.recaptcha}>
            <ReCAPTCHA
              sitekey={envVariables.NEXT_PUBLIC_RECAPTCHA_API_KEY}
              onChange={onChange}
              hl={lang}
            />
          </div>
          <Button
            onClick={formik.handleSubmit}
            variant='normal'
            disabled={!formik.isValid || !captcha}
          >
            {t('common:Apply')}
          </Button>
        </div>
      </div>
      <ErrorModal
        active={showError}
        icon='/assets/error.png'
        setActive={() => setShowError(false)}
      >
        {t('esim:common.errorTitle')}
      </ErrorModal>

      <SuccessModal
        active={showSuccess}
        title={t('fiber:modals.successTitle')}
        description={t('fiber:modals.successText')}
        setSuccessModal={() => setShowSuccess(false)}
      >
        {t('esim:common.successMessage')}
      </SuccessModal>
    </div>
  );
};

export default RequestNewAddress;
